import { useState, useRef, useEffect } from 'react'
import Footer from '@src/components/footer'
import Nav from '@src/components/nav'
import classNames from 'classnames'
import LoadingContainer from '@src/components/loading-container'
import { useOrientation } from '@src/hooks/use-orientation'
import Script from 'next/script'
import { useHTL } from '@src/hooks/htl'

export interface WatchLayoutProps {
  loading: boolean
  children: React.ReactNode
  sidebar?: React.ReactNode
  storyKeyValues?: any
}

export default function WatchLayout({
  children,
  sidebar,
  loading,
  storyKeyValues
}: WatchLayoutProps) {
  const videoContainerRef = useRef<HTMLDivElement>(null)
  const [playlistMargin, setPlaylistMargin] = useState(0)
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false)
  const orientation = useOrientation()

  function calculatePlaylistMargin() {
    return setPlaylistMargin(videoContainerRef.current?.getBoundingClientRect()?.height ?? 0)
  }

  useHTL(storyKeyValues, scriptLoaded)

  useEffect(() => {
    if (!sidebar) return
    calculatePlaylistMargin()
    window.addEventListener('resize', calculatePlaylistMargin)
    return () => {
      window.removeEventListener('resize', calculatePlaylistMargin)
    }
  }, [sidebar])

  return (
    <>
      <div className='flex flex-col w-full'>
        <div className='hidden lg:block'>
          <Nav type='on-demand' />
        </div>
        <div className='relative flex flex-col w-full h-full bg-black lg:justify-center lg:py-6 lg:gap-6 lg:px-6 lg:flex-row'>
          <LoadingContainer loading={loading}>
            <div
              className={classNames('w-full', {
                'lg:w-[calc(100vw_-_350px)]': sidebar !== undefined
              })}
            >
              {children && (
                <div
                  ref={videoContainerRef}
                  className={classNames('flex flex-col w-full lg:sticky lg:gap-6 lg:top-5', {
                    'fixed top-0 left-0 z-50': orientation !== 'landscape'
                  })}
                >
                  <div className='lg:hidden'>
                    <Nav type='on-demand' />
                  </div>
                  {children}
                </div>
              )}
            </div>
            {sidebar && (
              <div
                className={classNames('lg:w-[350px] w-full', {
                  'scrollable-playlist lg:mt-0': orientation !== 'landscape'
                })}
              >
                {sidebar}
                <Footer image={''} className='lg:hidden' />
              </div>
            )}
          </LoadingContainer>
        </div>
        <Footer image={''} className={classNames({ 'hidden lg:block': sidebar !== undefined })} />
      </div>
      <style>
        {`
        @media (max-width: 1023px) {
          .scrollable-playlist {
            margin-top: ${playlistMargin}px;
          }
        }
        `}
      </style>
      <Script
        onLoad={() => setScriptLoaded(true)}
        src={`https://htlbid.com${
          process.env.NODE_ENV === 'production' ? '/' : '/stage/'
        }v3/barstool.tv/htlbid.js`}
        strategy='afterInteractive'
      />
    </>
  )
}
